/**
 * Custom Checkout Redirect for Ukrainian Market
 * Redirects Ukrainian market customers to custom checkout app
 * International market uses default Shopify checkout
 */

class CustomCheckoutRedirect {
  constructor() {
    this.checkoutButtons = [];
    this.config = window.CUSTOM_CHECKOUT_CONFIG || {};
    this.debug = this.config.debug || false;
    this.customCheckoutUrl = this.getCustomCheckoutUrl();
    this.init();
  }

  getCustomCheckoutUrl() {
    // 1. Prioritize localhost for local theme development
    if (window.location.hostname === '127.0.0.1') {
      this.log('Localhost detected. Using local checkout URL.');
      // This is the URL of your local checkout app, not your theme preview.
      return 'http://localhost:3000/';
    }

    // --- Configuration ---
    const productionHostnames = ['dostavka.net.ua', 'www.dostavka.net.ua'];
    const productionCheckoutUrl = 'https://order.dostavka.net.ua';
    // This is for your remote dev environment (e.g., order.dostavka.net.ua)
    const developmentCheckoutUrl = 'http://localhost:3000/'; 

    // --- Logic ---
    // 2. Prioritize URL from theme config if it exists and is not a placeholder
    if (this.config.checkoutUrl && !this.config.checkoutUrl.includes('your-custom-checkout')) {
      this.log('Using checkout URL from theme config:', this.config.checkoutUrl);
      return this.config.checkoutUrl;
    }

    // 3. Otherwise, determine URL by hostname
    this.log('No theme config URL found or is placeholder. Detecting environment by hostname.');
    if (productionHostnames.includes(window.location.hostname)) {
      this.log('Production hostname detected. Using production checkout URL.');
      return productionCheckoutUrl;
    } else {
      this.log(`Development hostname detected (${window.location.hostname}). Using development checkout URL.`);
      // For hostnames like 'order.dostavka.net.ua' this will now correctly return the development URL
      return developmentCheckoutUrl;
    }
  }

  log(...args) {
    if (this.debug) {
      console.log('[Custom Checkout]', ...args);
    }
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setup());
    } else {
      this.setup();
    }
  }

  setup() {
    // Find all checkout buttons
    this.findCheckoutButtons();

    // Add click handlers
    this.attachHandlers();

    // Observe for dynamic content (cart drawer)
    this.observeDOM();

    // Check for buttons periodically (for dynamically loaded content)
    this.startPeriodicCheck();

    // Listen for any clicks on the document
    this.listenForClicks();

    this.log('Custom checkout redirect initialized');
  }

  startPeriodicCheck() {
    // Check every 500ms for new buttons
    this.checkInterval = setInterval(() => {
      const currentButtonCount = this.checkoutButtons.length;
      this.checkoutButtons = [];
      this.findCheckoutButtons();

      if (this.checkoutButtons.length !== currentButtonCount) {
        this.log(`Button count changed: ${currentButtonCount} -> ${this.checkoutButtons.length}`);
        this.attachHandlers();
      }
    }, 500);
  }

  listenForClicks() {
    this.log('Setting up event listeners for form submissions and clicks');

    // Intercept form submissions to cart
    document.addEventListener('submit', (e) => {
      this.log('FORM SUBMIT EVENT DETECTED:', e.target.id, e.target.action);

      const form = e.target;

      // Check if this is a cart form being submitted
      if (form.id === 'CartDrawer-Form' || form.id === 'cart' || form.action?.includes('/cart')) {
        this.log('Cart form submission detected:', form.id);

        // Check if there's a checkout button being clicked
        const submitter = e.submitter;
        this.log('Form submitter:', submitter?.name, submitter?.id, submitter?.tagName);

        if (submitter?.name === 'checkout' || submitter?.id?.includes('checkout') || submitter?.id?.includes('Checkout')) {
          this.log('✅ Checkout button submitted form');

          const isUkrainianMarket = this.isUkrainianMarket();
          this.log('Ukrainian market check:', isUkrainianMarket);

          if (isUkrainianMarket) {
            this.log('🚫 Preventing default and intercepting checkout');
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
            this.handleCheckout(e, submitter);
            return false;
          }
        }
      }
    }, true); // Use capture phase

    // Also intercept any clicks on checkout buttons directly
    document.addEventListener('click', (e) => {
      const target = e.target;

      // Log ALL clicks for debugging
      if (target.tagName === 'BUTTON') {
        this.log('Button clicked:', target.id, target.name, target.className);
      }

      // Check if the clicked element or any parent is a checkout button
      const checkoutButton = target.closest('button[name="checkout"]') ||
                            target.closest('#CartDrawer-Checkout') ||
                            target.closest('#checkout') ||
                            target.closest('.cart__checkout-button');

      if (checkoutButton) {
        this.log('✅ Checkout button clicked:', checkoutButton.id || checkoutButton.className);

        // Check if we have this button registered
        if (!this.checkoutButtons.includes(checkoutButton)) {
          this.log('Button not registered, adding now');
          this.checkoutButtons.push(checkoutButton);
        }

        // Handle the checkout
        const isUkrainianMarket = this.isUkrainianMarket();
        this.log('Ukrainian market check:', isUkrainianMarket);

        if (isUkrainianMarket) {
          this.log('🚫 Preventing default and intercepting checkout');
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          this.handleCheckout(e, checkoutButton);
          return false;
        }
      }
    }, true); // Use capture phase to catch it early

    this.log('Event listeners attached successfully');
  }

  findCheckoutButtons() {
    // Cart drawer checkout button
    const drawerButton = document.querySelector('#CartDrawer-Checkout');
    if (drawerButton) {
      this.checkoutButtons.push(drawerButton);
    }

    // Main cart page checkout button
    const mainButton = document.querySelector('#checkout');
    if (mainButton) {
      this.checkoutButtons.push(mainButton);
    }

    this.log(`Found ${this.checkoutButtons.length} checkout button(s)`);
  }

  attachHandlers() {
    this.checkoutButtons.forEach(button => {
      // Remove existing handlers to avoid duplicates
      button.removeEventListener('click', this.handleCheckoutClick);

      // Add click handler (use arrow function to preserve 'this' context)
      this.handleCheckoutClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.handleCheckout(e, button);
      };

      button.addEventListener('click', this.handleCheckoutClick, true); // Use capture phase

      // Also prevent form submission
      const form = button.form;
      if (form) {
        form.addEventListener('submit', (e) => {
          const isUkrainianMarket = this.isUkrainianMarket();
          if (isUkrainianMarket) {
            e.preventDefault();
            e.stopPropagation();
          }
        }, true);
      }
    });
  }

  async handleCheckout(event, button) {
    // Check if this is Ukrainian market
    const isUkrainianMarket = this.isUkrainianMarket();

    this.log('Checkout initiated. Ukrainian market:', isUkrainianMarket);

    if (isUkrainianMarket) {
      // Already prevented in click handler, but prevent again just in case
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }

      // Show loading state
      button.setAttribute('disabled', 'disabled');
      const originalText = button.textContent;
      button.textContent = 'Завантаження...';

      try {
        // Get cart data
        const cartData = await this.getCartData();

        this.log('Cart data:', cartData);

        // Clear cart immediately after getting data (before redirect)
        // This prevents the cart from staying populated if user doesn't complete checkout
        // await this.clearCart();
        // this.log('Cart cleared');

        // Redirect to custom checkout with cart data
        const checkoutUrl = this.buildCustomCheckoutUrl(cartData);
        this.log('Redirecting to:', checkoutUrl);
        window.location.href = checkoutUrl;
      } catch (error) {
        console.error('Error redirecting to custom checkout:', error);

        // Restore button and fallback to default checkout
        button.removeAttribute('disabled');
        button.textContent = originalText;
        alert('Помилка при переході до оформлення замовлення. Спробуйте ще раз.');
      }
    } else {
      // International market - allow default Shopify checkout
      this.log('Using default Shopify checkout for international market');
      // Allow the button/form to submit naturally
    }
  }

  isUkrainianMarket() {
    // Method 1: Use Shopify market data from config (most reliable)
    if (this.config.market) {
      const { country, language, handle } = this.config.market;
      this.log('Market detection:', { country, language, handle });

      // Check country code
      if (country === 'UA') {
        this.log('✅ Ukrainian market detected via country code');
        return true;
      }

      // Check language
      if (language && (language.startsWith('uk') || language.startsWith('ru'))) {
        this.log('✅ Ukrainian market detected via language');
        return true;
      }

      // Check market handle
      if (handle && (handle.includes('ukrain') || handle.includes('ua'))) {
        this.log('✅ Ukrainian market detected via market handle');
        return true;
      }
    }

    // Method 2: Check Shopify.country (legacy)
    if (window.Shopify && window.Shopify.country) {
      if (window.Shopify.country === 'UA') {
        this.log('✅ Ukrainian market detected via Shopify.country');
        return true;
      }
    }

    // Method 3: Check document language
    const htmlLang = document.documentElement.lang;
    if (htmlLang === 'uk' || htmlLang === 'ru' || htmlLang === 'uk-UA' || htmlLang === 'ru-UA') {
      this.log('✅ Ukrainian market detected via HTML lang attribute');
      return true;
    }

    // Method 4: Check URL for market parameter
    const url = new URL(window.location.href);
    const market = url.searchParams.get('market');
    if (market === 'UA' || market === 'uk') {
      this.log('✅ Ukrainian market detected via URL parameter');
      return true;
    }

    // Default: assume international if no Ukrainian indicators found
    this.log('❌ No Ukrainian market indicators found, defaulting to international');
    return false;
  }

  async getCartData() {
    try {
      const response = await fetch('/cart.js');
      if (!response.ok) throw new Error('Failed to fetch cart');

      const cart = await response.json();

      // Transform cart data to match custom checkout format
      return {
        id: cart.token, // Add cart token as the ID
        items: cart.items.map(item => ({
          id: `gid://shopify/ProductVariant/${item.variant_id}`,
          variantId: `gid://shopify/ProductVariant/${item.variant_id}`,
          productId: `gid://shopify/Product/${item.product_id}`,
          title: item.product_title,
          variant: item.variant_title || 'Стандартний',
          quantity: item.quantity,
          price: (item.price / 100).toFixed(2), // Convert from cents to UAH
          image: item.image || '',
          sku: item.sku || '',
          requiresShipping: item.requires_shipping,
          taxable: item.taxable,
          weight: item.grams,
          weightUnit: 'g'
        })),
        total: (cart.total_price / 100).toFixed(2),
        currency: cart.currency
      };
    } catch (error) {
      console.error('Error fetching cart data:', error);
      throw error;
    }
  }

  async clearCart() {
    try {
      const response = await fetch('/cart/clear.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        console.error('Failed to clear cart');
      }

      return await response.json();
    } catch (error) {
      console.error('Error clearing cart:', error);
      // Don't throw - cart clearing is not critical for redirect
    }
  }

  buildCustomCheckoutUrl(cartData) {
    // Encode cart data as URL parameter
    const cartParam = encodeURIComponent(JSON.stringify(cartData));
    const shopUrl = window.location.origin;
    const backToCartUrl = encodeURIComponent(`${shopUrl}/cart`);
    return `${this.customCheckoutUrl}?cart=${cartParam}&shop_url=${encodeURIComponent(shopUrl)}&back_to_cart_url=${backToCartUrl}`;
  }

  observeDOM() {
    // Watch for cart drawer being added/updated dynamically
    const observer = new MutationObserver((mutations) => {
      let shouldReattach = false;

      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) { // Element node
            // Check if it's cart drawer or contains checkout button
            if (node.id === 'CartDrawer' ||
                node.classList?.contains('cart-drawer') ||
                (node.querySelector && (node.querySelector('#CartDrawer-Checkout') || node.querySelector('#checkout')))) {
              shouldReattach = true;
            }
          }
        });
      });

      if (shouldReattach) {
        this.log('Cart drawer detected, re-attaching handlers');
        // Small delay to ensure DOM is fully ready
        setTimeout(() => {
          this.checkoutButtons = [];
          this.findCheckoutButtons();
          this.attachHandlers();
        }, 100);
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    // Also listen for cart drawer open events
    document.addEventListener('cart:open', () => {
      this.log('Cart opened event detected');
      setTimeout(() => {
        this.checkoutButtons = [];
        this.findCheckoutButtons();
        this.attachHandlers();
      }, 200);
    });

    // Listen for custom element defined (cart-drawer)
    if (window.customElements) {
      window.customElements.whenDefined('cart-drawer').then(() => {
        this.log('cart-drawer custom element defined');
        setTimeout(() => {
          this.checkoutButtons = [];
          this.findCheckoutButtons();
          this.attachHandlers();
        }, 100);
      });
    }
  }
}

// Initialize when script loads
new CustomCheckoutRedirect();
